/**
 * One-line hardware summaries for a case with several orders (#1035).
 * A single live order stays the full card; the header names the most urgent status among the rest.
 */

import { poText } from '../../lib/po'
import type { HardwareOrderOut, PoStatus, ProcurementStatus } from './api'
import { money } from './pricing'
import { PO_LABEL, PROCUREMENT_LABEL } from './status'

const RANK: { label: string; test: (order: HardwareOrderOut) => boolean }[] = [
  {
    label: 'awaiting Books approval',
    test: (order) => order.procurement_status === 'awaiting_approval' || order.purchase_order?.status === 'pending_approval',
  },
  {
    label: 'rejected in Books',
    test: (order) => order.procurement_status === 'rejected' || order.purchase_order?.status === 'rejected',
  },
  { label: 'shipped', test: (order) => order.procurement_status === 'shipped' },
  {
    label: 'partly received',
    test: (order) => order.procurement_status === 'partially_received' || order.purchase_order?.status === 'partially_received',
  },
  { label: 'staged at office', test: (order) => order.procurement_status === 'staged' },
  {
    label: 'ordered',
    test: (order) =>
      order.procurement_status === 'ordered' ||
      order.purchase_order?.status === 'approved' ||
      order.purchase_order?.status === 'issued' ||
      order.purchase_order?.status === 'draft',
  },
  {
    label: 'received',
    test: (order) => order.procurement_status === 'received' || order.purchase_order?.status === 'received',
  },
  { label: 'installed', test: (order) => order.procurement_status === 'installed' },
  {
    label: 'billed',
    test: (order) => order.procurement_status === 'billed' || order.purchase_order?.status === 'billed',
  },
]

/** "2 orders · 1 awaiting Books approval", or null when one card should stay open. */
export function hardwareHeaderSummary(orders: HardwareOrderOut[]): string | null {
  if (orders.length < 2) return null
  let best = RANK.length
  const counts = new Map<number, number>()
  for (const order of orders) {
    const rank = RANK.findIndex((bucket) => bucket.test(order))
    const index = rank === -1 ? RANK.length : rank
    counts.set(index, (counts.get(index) ?? 0) + 1)
    if (index < best) best = index
  }
  if (best === RANK.length) return `${orders.length} orders`
  return `${orders.length} orders · ${counts.get(best)} ${RANK[best]?.label}`
}

export function orderStatusLabel(order: HardwareOrderOut): string {
  const po = order.purchase_order?.status
  if (po && po in PO_LABEL) return PO_LABEL[po as PoStatus]
  const procurement = order.procurement_status
  if (procurement && procurement in PROCUREMENT_LABEL) return PROCUREMENT_LABEL[procurement as ProcurementStatus]
  return 'status unknown'
}

export function orderTotalLabel(order: HardwareOrderOut): string | null {
  const amount = order.purchase_order?.amount
  if (typeof amount !== 'string' || amount.trim() === '') return null
  return money(amount, order.purchase_order?.currency ?? 'CAD')
}

export function orderPoLabel(order: HardwareOrderOut): string {
  const number = order.purchase_order?.books_po_number || order.manual_po_number
  return poText(number) || 'PO not issued'
}
